import { useParams, Link } from 'react-router-dom';
import { categories, manhwaList } from '@/mocks/manhwa';
import { useState, useMemo } from 'react';
import { useSchemaOrg } from '@/hooks/useSchemaOrg';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'lianai');
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const categoryInfo = categories.find((c) => c.slug === activeCategory);

  const schemaOrg = categoryInfo ? {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryInfo.name}韩漫 - 韩漫网站`,
    url: `https://www.example.com/category/${activeCategory}`,
    description: categoryInfo.description,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫网站',
      url: 'https://www.example.com',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://www.example.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: `${categoryInfo.name}韩漫`,
        },
      ],
    },
  } : null;

  useSchemaOrg(schemaOrg);

  const filteredManhwa = useMemo(() => {
    let result = manhwaList.filter((m) => m.category === activeCategory);
    if (sortBy === 'popular') {
      result = [...result].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else {
      result = [...result].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
    }
    return result;
  }, [activeCategory, sortBy]);

  return (
    <div className="min-h-screen bg-background-50 pt-16">
      <div className="bg-background-100 border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
            <span>/</span>
            <span className="text-foreground-800 font-medium">{categoryInfo?.name || '分类'}</span>
          </nav>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
            {categoryInfo?.name}韩漫
          </h1>
          <p className="text-sm text-foreground-500 mt-2">
            {categoryInfo?.description}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="flex items-center gap-2 flex-wrap pb-4 border-b border-background-200/70 mb-6">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              onClick={() => setActiveCategory(cat.slug)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                activeCategory === cat.slug
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {cat.name}
            </Link>
          ))}
        </div>

        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-foreground-500">
            共 <strong className="text-foreground-900">{filteredManhwa.length}</strong> 部漫画
          </p>
          <div className="flex items-center gap-2">
            {(['latest', 'popular', 'rating'] as const).map((key) => (
              <button
                key={key}
                onClick={() => setSortBy(key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  sortBy === key
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {key === 'latest' ? '最新更新' : key === 'popular' ? '最受欢迎' : '评分最高'}
              </button>
            ))}
          </div>
        </div>

        {filteredManhwa.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
              <i className="ri-book-open-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500">该分类暂无漫画</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
            {filteredManhwa.map((manhwa) => (
              <Link
                key={manhwa.id}
                to={`/article/${manhwa.slug}`}
                className="group cursor-pointer"
              >
                <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-100">
                  <img
                    src={manhwa.cover}
                    alt={manhwa.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                    <div className="flex items-center gap-1">
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-star-fill text-amber-400 text-xs"></i>
                      </div>
                      <span className="text-white text-xs font-medium">{manhwa.rating}</span>
                      <span className="text-white/60 text-xs ml-auto">{manhwa.chapters}话</span>
                    </div>
                  </div>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                  {manhwa.title}
                </h3>
                <p className="text-xs text-foreground-500 mt-1">{manhwa.author}</p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}